import { SwingLabVideo } from '../data/useRecentVideos';
import { isWithinSwingLabLimits, getLimitWarningMessage } from '../utils/limits';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Play, AlertTriangle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface VideoListProps {
  videos: SwingLabVideo[];
  onPlay: (id: string) => void;
  isLoading?: boolean;
}

function getStatusColor(status?: string) {
  switch (status) {
    case 'ready':
      return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    case 'processing':
      return 'bg-amber-100 text-amber-800 border-amber-200';
    case 'failed':
    case 'errored':
      return 'bg-red-100 text-red-800 border-red-200';
    default:
      return 'bg-slate-100 text-slate-800 border-slate-200';
  }
}

function formatDuration(seconds?: number | null): string {
  if (!seconds) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function VideoList({ videos, onPlay, isLoading }: VideoListProps) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-white">
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-slate-200 rounded w-1/3"></div>
                <div className="h-3 bg-slate-200 rounded w-1/4"></div>
              </div>
              <div className="h-8 w-16 bg-slate-200 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="text-slate-500 text-sm">No videos found</div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {videos.map((video) => {
        const withinLimits = isWithinSwingLabLimits(video);
        const warningMessage = getLimitWarningMessage(video);
        const canPlay = video.status === 'ready' && video.muxPlaybackId;

        return (
          <div key={video.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-white hover:border-slate-300 transition-colors">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium text-slate-900 truncate">
                  {video.title || `Video ${video.id.slice(0, 8)}`}
                </span>
                
                <Badge className={`text-xs ${getStatusColor(video.status)}`}>
                  {video.status || 'unknown'}
                </Badge>
                
                {!withinLimits && warningMessage && (
                  <Badge className="text-xs bg-amber-100 text-amber-800 border-amber-200 inline-flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    <span className="hidden sm:inline">{warningMessage}</span>
                    <span className="sm:hidden">Limits</span>
                  </Badge>
                )}
              </div>
              
              <div className="text-xs text-slate-600 space-x-3">
                <span>{formatDistanceToNow(new Date(video.createdAt), { addSuffix: true })}</span>
                <span>•</span>
                <span>{formatDuration(video.durationSec)}</span>
                {video.resolution && (
                  <>
                    <span>•</span>
                    <span>{video.resolution}</span>
                  </>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-2 ml-3">
              <Button
                size="sm"
                variant={canPlay ? "default" : "secondary"}
                disabled={!canPlay}
                onClick={() => canPlay && onPlay(video.id)}
                className="h-8 px-3"
              >
                <Play className="h-3.5 w-3.5 mr-1" />
                Play
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}